import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { GatewayAddressSchema, ServiceKeySchema } from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  HelpHint,
  Input,
  Select,
} from '@aeris/ui'

import {
  ApiError,
  createChannelAccount,
  updateChannelAccount,
  type ChannelAccountSummaryType,
} from '../lib/api.ts'

/**
 * Cadastra ou edita a conta de WhatsApp que fala com o gateway (OpenBSP), F045.
 *
 * ## O que não pode mudar depois de criada
 *
 * A organização no OpenBSP e o número são a identidade da conta — é por eles
 * que `communication_external_index` acha o tenant quando um webhook chega
 * sem sessão nenhuma (F045). Editá-los depois de criada apontaria o índice
 * para o par errado, então os dois campos ficam desabilitados ao editar, como
 * `channel-form.tsx` já faz com tipo e código de um canal de marketplace.
 *
 * ## Os dois segredos nunca voltam
 *
 * Service key e segredo do webhook são campos de escrita: `hasCredentials` é
 * tudo que a conta diz sobre eles — presença, não valor, o mesmo padrão da
 * F032. Os campos começam vazios mesmo quando já existe algo salvo, e o hint
 * diz isso; ao editar, deixar em branco significa "não mexa".
 *
 * ## O segredo do webhook é obrigatório ao criar
 *
 * O corpo do webhook do OpenBSP É o dado — diferente do Mercado Livre, onde o
 * corpo é só uma campainha e o pedido é buscado de volta com credencial
 * própria. Aceitar um evento sem verificar a credencial é aceitar que um
 * estranho que descobriu o número da loja escreva na caixa de entrada de um
 * cliente, então `ChannelAccountCreate.webhookSecret` deixou de aceitar nulo:
 * é a única credencial exigida já na criação. `serviceKey` continua opcional
 * — uma conta pode ser cadastrada antes de o gateway estar pronto —, e o
 * campo abaixo avisa quando isso significa que o segredo digitado ainda não
 * fica salvo (ver o comentário sobre `body.serviceKey !== null` em
 * `apps/api/src/routes/v1/communication.ts`).
 *
 * ## `channel_secret_missing`
 *
 * Toda escrita nesta conta — mesmo trocar só o nome — exige `CHANNEL_SECRET_
 * KEY` configurada no servidor, porque a rota abre a chave de cifra antes de
 * tocar qualquer coluna. Quando a variável não está lá, a API responde 409
 * com uma frase pronta nomeando a variável, e esta tela mostra essa frase como
 * orientação — um aviso, não um erro genérico — em vez de "algo deu errado".
 */

/**
 * Uma fábrica, não um objeto fixo: o segredo do webhook é obrigatório ao
 * criar e opcional ao editar (`ChannelAccountUpdate` aceita nulo — "não mexa
 * nas credenciais desta vez"). `editing` não muda depois que o diálogo monta
 * — o chamador troca a `key` do componente ao alternar entre criar e editar
 * (ver `atendimento-canais.tsx`) —, então uma fábrica lida no `useForm` é
 * suficiente; não precisa ser reativa.
 */
const buildSchema = (editing: boolean) =>
  z.object({
    displayName: z.string().trim().min(1, 'Informe o nome').max(120),
    openbspOrgId: z
      .string()
      .trim()
      .min(1, 'Informe a organização no OpenBSP')
      .max(120),
    address: GatewayAddressSchema,
    /*
     * String, não `z.coerce.number()`: o par resolver/useForm do
     * react-hook-form não reconcilia um schema cujo tipo de ENTRADA
     * (`unknown`, o que `coerce` aceita) difere do de SAÍDA (`number`) sob
     * `exactOptionalPropertyTypes` — o erro é no compilador, não em tempo de
     * execução, mas é real. A conversão para número acontece na mutação,
     * como os campos numéricos de `channel-form.tsx` já fazem.
     */
    throughputMps: z
      .string()
      .trim()
      .regex(/^\d+$/, 'Use um número inteiro.')
      .refine((value) => {
        const parsed = Number(value)
        return parsed >= 1 && parsed <= 1000
      }, 'Use um valor entre 1 e 1000.'),
    status: z.enum(['active', 'disconnected']),
    /*
     * Vazio é permitido — a conta pode ser cadastrada antes de a chave existir
     * —, então o `min(1)` do contrato não serve aqui. O que importa é a
     * recusa do JWT, e essa vale nos dois lugares: `ServiceKeySchema` é a
     * mesma regra, aplicada só quando há algo digitado.
     */
    serviceKey: z
      .string()
      .trim()
      .max(500)
      .refine(
        (value) => value === '' || ServiceKeySchema.safeParse(value).success,
        'Isso é a chave da instalação (a publishable key, o JWT que começa com "eyJ"), não a chave desta organização. A certa aparece logo abaixo dela no painel do OpenBSP e começa com "sk_".'
      ),
    webhookSecret: editing
      ? z.string().trim().max(500)
      : z.string().trim().min(1, 'Informe o segredo do webhook').max(500),
  })

type FormValues = z.infer<ReturnType<typeof buildSchema>>

const STATUS_HINTS: Record<FormValues['status'], string> = {
  active: 'Pronta para receber e enviar mensagens.',
  disconnected:
    'O vínculo com o WhatsApp caiu. Reconecte no WhatsApp Business e volte para Ativa.',
}

export type ChannelAccountFormProps = {
  open: boolean
  /** Absent means "create". */
  account?: ChannelAccountSummaryType | undefined
  onOpenChange: (open: boolean) => void
  onSaved: () => Promise<void> | void
}

export const ChannelAccountForm = ({
  open,
  account,
  onOpenChange,
  onSaved,
}: ChannelAccountFormProps): ReactNode => {
  const editing = account !== undefined
  const hasCredentials = account?.hasCredentials ?? false
  const [formError, setFormError] = useState<string | null>(null)
  const [needsSecretKey, setNeedsSecretKey] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(buildSchema(editing)),
    defaultValues: {
      displayName: account?.displayName ?? '',
      openbspOrgId: account?.openbspOrgId ?? '',
      address: account?.address ?? '',
      throughputMps: String(account?.throughputMps ?? 20),
      // `pairing` é só do aparelho vinculado (F192), que não abre esta tela.
      status: account?.status === 'disconnected' ? 'disconnected' : 'active',
      serviceKey: '',
      webhookSecret: '',
    },
  })

  // A situação só existe para editar: uma conta nova nasce ativa no servidor
  // (ChannelAccountCreate não tem o campo), e o hint segue o que a pessoa está
  // escolhendo agora — `useWatch`, nunca `watch()`, para não desligar o
  // memoisador do compilador.
  const status = useWatch({ control, name: 'status' })

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const serviceKey =
        values.serviceKey.trim() === '' ? null : values.serviceKey.trim()
      const throughputMps = Number(values.throughputMps)

      if (editing) {
        await updateChannelAccount(account.id, {
          displayName: values.displayName.trim(),
          status: values.status,
          throughputMps,
          serviceKey,
          // Nulo aqui significa "não mexa" — `ChannelAccountUpdate` aceita.
          webhookSecret:
            values.webhookSecret.trim() === ''
              ? null
              : values.webhookSecret.trim(),
          version: account.version,
        })
        return
      }

      await createChannelAccount({
        kind: 'whatsapp',
        openbspOrgId: values.openbspOrgId.trim(),
        address: values.address.trim(),
        displayName: values.displayName.trim(),
        throughputMps,
        serviceKey,
        // Garantido não vazio pelo schema quando `!editing` —
        // `ChannelAccountCreate.webhookSecret` não aceita nulo.
        webhookSecret: values.webhookSecret.trim(),
      })
    },
    onSuccess: async () => {
      setFormError(null)
      setNeedsSecretKey(false)
      await onSaved()
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      const missingSecret =
        cause instanceof ApiError && cause.code === 'channel_secret_missing'
      setNeedsSecretKey(missingSecret)
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar esta conta.'
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    setNeedsSecretKey(false)
    save.mutate(values)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={editing ? 'Editar conta de canal' : 'Nova conta de canal'}
        description="A conta de WhatsApp que envia e recebe mensagens pelo gateway (OpenBSP)."
        className="max-w-lg"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            void onSubmit(event)
          }}
        >
          <Field
            label="Nome de exibição"
            required
            error={errors.displayName?.message}
          >
            {(field) => (
              <Input
                {...field}
                {...register('displayName')}
                autoFocus
                placeholder="WhatsApp da loja"
              />
            )}
          </Field>

          {/*
            Identidade fixa: são estes dois campos que
            `communication_external_index` usa para achar o tenant quando um
            webhook chega sem sessão. Trocá-los depois apontaria o índice para
            o par errado.
          */}
          <Field
            label="Organização no OpenBSP"
            required
            error={errors.openbspOrgId?.message}
            hint={
              editing
                ? 'Não muda depois de criada.'
                : 'O identificador da organização cadastrada no gateway.'
            }
          >
            {(field) => (
              <Input
                {...field}
                {...register('openbspOrgId')}
                disabled={editing}
                placeholder="org_..."
              />
            )}
          </Field>

          <Field
            label="ID do número (OpenBSP)"
            required
            error={errors.address?.message}
            hint={
              editing
                ? 'Não muda depois de criada.'
                : 'O "ID do número" que o OpenBSP mostra na integração — não o telefone.'
            }
            help={({ id }) => (
              <HelpHint label="ID do número (OpenBSP)" id={id}>
                É por este identificador que o Aeris descobre de quem é cada
                mensagem que chega, porque é ele que o gateway carrega em todo
                evento — o telefone não vai junto.
                <br />
                <br />
                No OpenBSP: <strong>Integrações → WhatsApp</strong>, campo
                &quot;ID do número&quot;. É um número longo (uns dezesseis
                dígitos), diferente do telefone da loja.
                <br />
                <br />
                Errar aqui não dá erro nenhum: as mensagens chegam, ficam
                gravadas como de origem desconhecida, e o inbox continua vazio.
              </HelpHint>
            )}
          >
            {(field) => (
              <Input
                {...field}
                {...register('address')}
                disabled={editing}
                inputMode="numeric"
                placeholder="1235188929669631"
                className="font-(--font-mono)"
              />
            )}
          </Field>

          {editing ? (
            <Field label="Situação" hint={STATUS_HINTS[status]}>
              {(field) => (
                <Select {...field} {...register('status')}>
                  <option value="active">Ativa</option>
                  <option value="disconnected">Desconectada</option>
                </Select>
              )}
            </Field>
          ) : null}

          <Field
            label="Alcance (mensagens por segundo)"
            error={errors.throughputMps?.message}
            hint="20 é o teto da coexistência do WhatsApp. Só muda ao migrar para a Cloud API pura."
          >
            {(field) => (
              <Input
                {...field}
                {...register('throughputMps')}
                inputMode="numeric"
              />
            )}
          </Field>

          <Field
            label="Service key"
            hint={
              hasCredentials
                ? 'Configurada. Deixe em branco para manter a atual.'
                : editing
                  ? 'A chave de API da organização no OpenBSP, para o Aeris falar com o gateway.'
                  : 'A chave de API da organização no OpenBSP. Pode ficar em branco por ora — mas, sem ela, o segredo do webhook abaixo também não é salvo agora; volte e preencha os dois juntos quando a chave estiver pronta.'
            }
            help={({ id }) => (
              <HelpHint label="Service key" id={id}>
                A credencial que o Aeris usa para CHAMAR o gateway (PostgREST do
                OpenBSP) — enviar mensagem, marcar como lida. Sem ela, a conta
                só recebe; nada sai.
                <br />
                <br />É a <strong>chave de API da organização</strong>, criada
                no painel do OpenBSP em Settings → API Keys. Não é o token de
                acesso da Meta (esse fica dentro do OpenBSP, que é quem fala com
                a Meta), nem a <code>service_role</code> do Supabase, nem a
                publishable key — essa última é da instalação inteira e vive no
                servidor, em <code>OPENBSP_PUBLISHABLE_KEY</code>.
              </HelpHint>
            )}
          >
            {(field) => (
              <Input
                {...field}
                {...register('serviceKey')}
                type="password"
                autoComplete="off"
                placeholder={hasCredentials ? '••••••••' : ''}
              />
            )}
          </Field>

          <Field
            label="Segredo do webhook"
            required={!editing}
            error={errors.webhookSecret?.message}
            hint={
              hasCredentials
                ? 'Configurado. Deixe em branco para manter o atual.'
                : editing
                  ? 'O token que o OpenBSP apresenta ao entregar um evento.'
                  : 'O token que o OpenBSP apresenta ao entregar um evento. Sem ele, o Aeris recusa toda mensagem — é o que garante que ela veio do gateway, e não de um estranho que descobriu o número da loja. Cadastre o mesmo valor no campo "Token" do webhook, lá no OpenBSP.'
            }
            help={({ id }) => (
              <HelpHint label="Segredo do webhook" id={id}>
                O OpenBSP manda este valor em cada evento, no cabeçalho{' '}
                <code>Authorization: Bearer</code>. Sem ele, todo evento é
                recusado — é o que garante que quem mandou foi o gateway, não um
                estranho que descobriu o número da loja. Gere algo longo e
                aleatório e trate como senha: é um token fixo, não uma
                assinatura, então quem o obtiver o usa até você trocá-lo nos
                dois lados. O mesmo valor vai no campo &quot;Token&quot; do
                registro do webhook, no OpenBSP.
              </HelpHint>
            )}
          >
            {(field) => (
              <Input
                {...field}
                {...register('webhookSecret')}
                type="password"
                autoComplete="off"
                placeholder={hasCredentials ? '••••••••' : ''}
              />
            )}
          </Field>

          {formError === null ? null : needsSecretKey ? (
            // Orientação, não erro genérico: o padrão da F032 para uma chave
            // de ambiente que falta é uma frase dizendo o nome dela, e a tela
            // não pode enterrar isso num vermelho que parece um bug.
            <p
              role="alert"
              className="flex items-start gap-2 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--text-primary)"
            >
              <TriangleAlert
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
              />
              <span>{formError}</span>
            </p>
          ) : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {formError}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
